import { Spacing16 } from 'autogen/design-tokens/tokens';
import {
    type ProductTabModel,
    ProductTabContentDiscriminator as ProductTabModelcontentDiscriminator
} from 'autogen/swagger/all';
import { RotateAnimation } from 'components/generic/animation';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import { ProductAccordionContext } from 'components/product/accordion/hooks/context';
import AccordionAnimation from 'components/shared/accordionAnimation';
import { rem } from 'modules/helpers/style';
import { type HTMLAttributes, type ReactNode, useContext, useRef } from 'react';
import styled, { css } from 'styled-components';
import { activeClassName } from '../shared/variables';
import BlockList from './blockList';
import ContentBrandTeaser from './contentBrandTeaser';
import DeliveryAndReturn from './deliveryAndReturn';
import Description from './description';
import Highlights from './highlights';
import PropertyList from './propertyList';
import TabIconList from './TabIconList/TabIconList';
import Table from './table';
import TableList from './TableList';
import Usage from './usage/Usage';

const ContentContainer = styled.div`
    padding-bottom: 0.1px; // 0.1px = reactSpring hack

    ${({ theme }) => (theme as any)?.ProductAccordionItem?.ContentContainer}
`;

const AccordionButton = styled.button`
    background-color: transparent;
    border: 0;
    padding-top: ${Spacing16};
    padding-bottom: ${Spacing16};
    width: 100%;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:focus,
    &:active {
        outline: none;
    }

    ${({ theme }) => (theme as any)?.ProductAccordionItem?.AccordionButton}
`;

const StyledBody = styled(Typography.Body)`
    margin: 0;
    ${({ theme }) => (theme as any)?.ProductAccordionItem?.StyledBody}
`;

const Accordion = styled.div``;

const StyledRotateAnimation = styled(RotateAnimation)`
    width: ${rem(16)};
    height: ${rem(16)};
`;

const StyledAccordionAnimation = styled(AccordionAnimation)<{ collapsed: boolean }>`
    ${({ collapsed }) =>
        !collapsed &&
        css`
            height: auto;
        `}
`;

function getProductTabs(productTab: ProductTabModel) {
    const { content, type } = productTab;

    return ProductTabModelcontentDiscriminator<ReactNode>(
        content,
        (props) => <BlockList {...props} />,
        (props) => <ContentBrandTeaser {...props} />,
        (props) => <DeliveryAndReturn {...props} />,
        (props) => <Description {...props} />,
        (props) => <Highlights {...props} />,
        (props) => <TabIconList {...props} />,
        (props) => <PropertyList {...props} />,
        (props) => <TableList {...props} />,
        (props) => <Table {...props} type={type} />,
        (props) => <Usage {...props} />,
        () => null
    );
}

interface IProductAccordionItem extends ProductTabModel {
    animate: boolean;
    className?: HTMLAttributes<HTMLDivElement>['className'];
}

export default function ProductAccordionItem(props: IProductAccordionItem): JSX.Element {
    const { title, type, isActive, animate, className } = props;
    const handleTabChange = useContext(ProductAccordionContext);
    const ref = useRef<HTMLDivElement>(null);

    const handleClick = async (tabType) => {
        await handleTabChange(tabType);

        setTimeout(() => {
            const offsetTop = ref.current?.offsetTop;

            window.scrollTo({
                top: offsetTop ? offsetTop - 140 : 0, // 140 is to prevent the tab title from hiding under header on mobile
                left: 0,
                behavior: 'smooth'
            });
        }, 100);
    };

    return (
        <Accordion key={type} ref={ref} className={className}>
            <AccordionButton
                type="button"
                onClick={() => {
                    handleClick(type);
                }}
            >
                <StyledBody size="L" strong>
                    {title}
                </StyledBody>
                <StyledRotateAnimation from="down" to="up" playAnimation={isActive}>
                    <Icon type="ChevronDown" size={16} />
                </StyledRotateAnimation>
            </AccordionButton>
            <StyledAccordionAnimation collapsed={!isActive} springConfig={{ duration: animate ? undefined : 0 }}>
                <ContentContainer className={isActive && activeClassName}>{getProductTabs(props)}</ContentContainer>
            </StyledAccordionAnimation>
        </Accordion>
    );
}
